import React from "react";
import { faFaceParty, faTimes } from "@fortawesome/pro-regular-svg-icons";
import {
    Popover,
    PopoverClose,
    PopoverContent,
    PopoverTrigger,
} from "@singlestore/fusion/components/popover/popover";
import {
    Step,
    StepIndicator,
} from "@singlestore/fusion/components/stepper/step";
import { Stepper } from "@singlestore/fusion/components/stepper/stepper";
import { Paragraph } from "@singlestore/fusion/components/typography";
import {
    Sentiment,
    useWasThisHelpfulContext,
} from "contexts/WasThisHelpfulContext";

import { Button, button } from "components/Button/Button";
import { FaIcon } from "components/FaIcon/FaIcon";
import { Input } from "components/Input/Input";
import { Link } from "components/Link/Link";

import { sendFeedbackEvent } from "utils/feedback";

import "./WasThisHelpfulPopover.scss";

const MIN_MESSAGE_LENGTH = 5;

const multipleChoiceOptions: Record<string, Array<string>> = {
    positive: ["Organization", "Content", "Instructions/Examples", "Other"],
    negative: [
        "Organization was confusing",
        "Content was unclear",
        "Instructions did not work",
        "Other",
    ],
};

type FeedbackStep = {
    title: string;
    body: React.ReactNode;
    navigable?: boolean;
};

const FEEDBACK_STEPS: Record<Sentiment, Array<FeedbackStep>> = {
    positive: [
        {
            title: "Select a reason",
            body: (
                <MultipleChoiceBody
                    header="Yes, thanks to the:"
                    options={multipleChoiceOptions.positive}
                />
            ),
        },
        {
            title: "Give more details",
            body: <FeedbackCommentBody header="Can you be more specific?" />,
        },
        { title: "Thank you", body: <ThankYouBody />, navigable: false },
    ],
    negative: [
        {
            title: "Select a reason",
            body: (
                <MultipleChoiceBody
                    header="No, because:"
                    options={multipleChoiceOptions.negative}
                />
            ),
        },
        {
            title: "Give more details",
            body: <FeedbackCommentBody header="Can you be more specific?" />,
        },
        { title: "Thank you", body: <ThankYouBody />, navigable: false },
    ],
    neutral: [
        {
            title: "Give some details",
            body: <FeedbackCommentBody header="I have a suggestion" />,
        },
        { title: "Thank you", body: <ThankYouBody />, navigable: false },
    ],
};

type FeedbackCommentBodyProps = {
    header: string;
};

function FeedbackCommentBody({ header }: FeedbackCommentBodyProps) {
    const { setPage, sentiment, reason } = useWasThisHelpfulContext();

    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [comment, setComment] = React.useState<string>("");
    const [email, setEmail] = React.useState<string>("");

    const handleFormSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        setErrorMessage("");

        if (comment.length < MIN_MESSAGE_LENGTH) {
            setErrorMessage("Feedback is too short");
            return false;
        }

        sendFeedbackEvent("wtah_click", {
            feedback_type: "comment",
            sentiment: sentiment as Sentiment,
            reason: reason as string,
            feedback: comment,
            email,
        });

        setPage((p) => p + 1);
    };

    let error;

    if (errorMessage) {
        error = (
            <Paragraph variant="body-2" my="2x" color="red-1">
                {errorMessage}
            </Paragraph>
        );
    }

    return (
        <div className="popover-content">
            <form onSubmit={handleFormSubmit}>
                <Paragraph variant="heading-1">{header}</Paragraph>

                {error}

                <Input
                    type="textarea"
                    rows={4}
                    value={comment}
                    setValue={setComment}
                />

                <Input
                    type="email"
                    placeholder="Email Address (optional)"
                    helperText="This is in case you’d like us to get back to you"
                    value={email}
                    setValue={setEmail}
                />

                <Button variation="secondary" type="submit">
                    Submit
                </Button>
            </form>
        </div>
    );
}

type MultipleChoiceBodyProps = {
    header: string;
    options: Array<string>;
};

function MultipleChoiceBody({ header, options }: MultipleChoiceBodyProps) {
    const { setPage, setReason, sentiment } = useWasThisHelpfulContext();

    const handleOptionClick = async (option: string) => {
        setReason(option);

        // our custom backend
        sendFeedbackEvent("wtah_click", {
            feedback_type: "reason",
            reason: option,
            sentiment: sentiment as Sentiment,
        });

        setPage((p) => p + 1);
    };

    return (
        <div className="popover-content">
            <Paragraph variant="heading-1" mb="1-5x">
                {header}
            </Paragraph>

            <ul className="feedback-list">
                {options.map((option) => (
                    <li key={option}>
                        <Button
                            variation="secondary"
                            onClick={() => handleOptionClick(option)}
                        >
                            {option}
                        </Button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function ThankYouBody() {
    return (
        <div className="popover-content thank-you-content">
            <FaIcon icon={faFaceParty} />

            <Paragraph variant="heading-1">Thanks for the feedback!</Paragraph>

            <Paragraph>
                Need help? Check the{" "}
                <Link to="https://support.singlestore.com/hc/en-us">
                    Help Center
                </Link>{" "}
                or ask the{" "}
                <Link to="https://www.singlestore.com/forum/">Communitiy</Link>{" "}
                for help.
            </Paragraph>
        </div>
    );
}

type WasThisHelpfulPopoverProps = {
    sentiment: Sentiment;
    onClick?: () => void;
    triggerContent: React.ReactNode | Array<React.ReactNode>;
    triggerLabel?: string;
};

export function WasThisHelpfulPopover({
    sentiment,
    onClick,
    triggerContent,
    triggerLabel,
}: WasThisHelpfulPopoverProps) {
    const {
        popoverOpen,
        setPopoverOpen,
        page,
        setPage,
        setSentiment,
        setReason,
    } = useWasThisHelpfulContext();

    React.useEffect(() => {
        if (!popoverOpen) {
            setPage(0);
            setSentiment(null);
            setReason(null);
        }
    }, [popoverOpen, setPage, setSentiment, setReason]);

    const handleClick = () => {
        onClick?.();
    };

    const navigableSteps = FEEDBACK_STEPS[sentiment].filter(
        ({ navigable }) => navigable !== false
    );
    let pagination;

    if (page < navigableSteps.length && navigableSteps.length > 1) {
        // don't show pagination on final page or when there's only 1 page

        pagination = (
            <Stepper variant="subdued" index={page} justifyContent="center">
                {navigableSteps.map((step) => (
                    <Step key={step.title} title={step.title} clickable={false}>
                        <StepIndicator />
                    </Step>
                ))}
            </Stepper>
        );
    }

    const triggerClasses = button({
        variation: "ghost-primary",
        size: "md",
        className: "was-this-helpful-button",
    });

    return (
        <Popover onOpenChange={(open) => setPopoverOpen(open)}>
            <PopoverTrigger
                className={triggerClasses}
                aria-label={triggerLabel}
                onClick={handleClick}
            >
                {triggerContent}
            </PopoverTrigger>
            <PopoverContent className="was-this-helpful-popover">
                <PopoverClose>
                    <FaIcon icon={faTimes} />
                </PopoverClose>

                {FEEDBACK_STEPS[sentiment]?.[page]?.body}

                {pagination}
            </PopoverContent>
        </Popover>
    );
}
